import {
  Button,
  CodeSnippet,
  InlineNotification,
  Link,
  Modal,
  SkeletonText,
  Stack,
  Tag,
  TextInput,
} from '@carbon/react';
import { asProblem, PageHeader, problemMessage } from '@pos/admin-ui';
import type { HoComponents } from '@pos/api-client';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi, useSession } from '../api';
import './signIn.scss';

type Setup = HoComponents['schemas']['TotpSetupDto'];
const statusKey = ['ho', 'me', 'sign-in'] as const;

/** The key in groups of four, as authenticator apps show it and people type it. */
const grouped = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

/**
 * Two-step sign-in for the signed-in staff member (ADR 0121): set up an authenticator app with its key, confirm it with a first
 * code, keep the recovery codes shown once; or turn it off from a session signed in with it. Sensitive permissions (personal data,
 * money, access) work only in a session signed in with a code or with the company's provider asking for one.
 */
export function TwoStepPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const session = useSession((s) => s.session);
  const status = useQuery({
    queryKey: statusKey,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/me/sign-in');
      if (!data) throw error;
      return data;
    },
  });
  const [setup, setSetup] = useState<Setup>();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[]>();
  const [turningOff, setTurningOff] = useState(false);

  const start = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/identity/me/mfa/totp');
      if (!data) throw error;
      return data;
    },
    onSuccess: setSetup,
  });
  const confirm = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.POST('/api/v1/identity/me/mfa/totp/confirm', { body: { code } });
      if (!data) throw error;
      return data;
    },
    onSuccess: (done) => {
      setSetup(undefined);
      setCode('');
      setRecovery([...done.recoveryCodes]);
      useSession.getState().start(done.session); // signed in with the code just given
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });
  const turnOff = useMutation({
    mutationFn: async () => {
      const { error, response } = await hoApi.DELETE('/api/v1/identity/me/mfa');
      if (!response.ok) throw error;
    },
    onSuccess: () => {
      setTurningOff(false);
      void queryClient.invalidateQueries({ queryKey: statusKey });
    },
  });

  if (status.isLoading) return <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />;
  if (!status.data)
    return (
      <InlineNotification
        kind="error"
        role="alert"
        lowContrast
        hideCloseButton
        title={problemMessage(t, asProblem(status.error))}
      />
    );
  const error = start.error ?? confirm.error;

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('signIn.twoStep.title')} description={t('signIn.twoStep.hint')} />
      <Stack gap={6}>
        <div>
          <Tag type={status.data.enabled ? 'green' : 'gray'}>
            {status.data.enabled ? t('signIn.twoStep.on') : t('signIn.twoStep.off')}
          </Tag>
          {status.data.enabled && (
            <p className="pos-sign-in__meta">
              {t('signIn.twoStep.recoveryLeft', { count: status.data.recoveryCodesLeft })}
            </p>
          )}
        </div>

        {recovery && (
          <Stack gap={4}>
            <InlineNotification
              kind="success"
              role="status"
              lowContrast
              hideCloseButton
              title={t('signIn.twoStep.done')}
            />
            <p>{t('signIn.twoStep.recoveryHint')}</p>
            <CodeSnippet type="multi" feedback={t('signIn.copied')}>
              {recovery.join('\n')}
            </CodeSnippet>
            <div>
              <Button kind="secondary" onClick={() => setRecovery(undefined)}>
                {t('signIn.twoStep.saved')}
              </Button>
            </div>
          </Stack>
        )}

        {!status.data.enabled && !setup && !recovery && (
          <div>
            <Button onClick={() => start.mutate()} disabled={start.isPending}>
              {t('signIn.twoStep.setUp')}
            </Button>
          </div>
        )}

        {setup && (
          <Stack gap={5}>
            <p>{t('signIn.twoStep.step1')}</p>
            <CodeSnippet type="single" feedback={t('signIn.copied')} aria-label={t('signIn.twoStep.key')}>
              {grouped(setup.secret)}
            </CodeSnippet>
            <Link href={setup.otpauthUri}>{t('signIn.twoStep.openApp')}</Link>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                if (code.trim()) confirm.mutate();
              }}
            >
              <Stack gap={5}>
                <TextInput
                  id="two-step-code"
                  labelText={t('signIn.twoStep.step2')}
                  value={code}
                  autoComplete="one-time-code"
                  onChange={(e) => setCode(e.target.value)}
                />
                <div>
                  <Button type="submit" disabled={!code.trim() || confirm.isPending}>
                    {t('signIn.twoStep.confirm')}
                  </Button>
                </div>
              </Stack>
            </form>
          </Stack>
        )}

        {status.data.enabled && !recovery && (
          <div>
            <Button kind="danger--tertiary" onClick={() => setTurningOff(true)}>
              {t('signIn.twoStep.turnOff')}
            </Button>
          </div>
        )}

        {error && (
          <InlineNotification
            kind="error"
            role="alert"
            lowContrast
            hideCloseButton
            title={problemMessage(t, asProblem(error))}
          />
        )}
        {!session?.mfa && status.data.enabled && (
          <InlineNotification kind="info" lowContrast hideCloseButton title={t('signIn.twoStep.signInAgain')} />
        )}
      </Stack>

      {turningOff && (
        <Modal
          open
          danger
          modalHeading={t('signIn.twoStep.turnOffConfirm')}
          primaryButtonText={t('signIn.twoStep.turnOff')}
          secondaryButtonText={t('common.cancel')}
          closeButtonLabel={t('carbon.close')}
          onRequestClose={() => {
            turnOff.reset();
            setTurningOff(false);
          }}
          onRequestSubmit={() => turnOff.mutate()}
        >
          <p>{t('signIn.twoStep.turnOffBody')}</p>
          {turnOff.error && (
            <InlineNotification
              kind="error"
              role="alert"
              lowContrast
              hideCloseButton
              title={problemMessage(t, asProblem(turnOff.error))}
            />
          )}
        </Modal>
      )}
    </section>
  );
}
